type StyleHost = Document | ShadowRoot;

/**
 * Creates a constructed stylesheet from a readonly one
 */
const constructStylesheet = (sheet: CSSStyleSheet): CSSStyleSheet => {
  const constructed = new CSSStyleSheet();
  try {
    constructed.replaceSync(
      Array.from(sheet.cssRules)
        .map(rule => {
          try {
            return rule.cssText;
          } catch {
            return ''; // Skip malformed rules
          }
        })
        .filter(Boolean)
        .join('\n')
    );
  } catch (error) {
    console.error('Failed to construct stylesheet:', error);
  }
  return constructed;
};

const processStylesheet = (
  cache: Map<string, CSSStyleSheet>,
  link: HTMLLinkElement
): boolean => {
  const {href, sheet} = link;

  if (!sheet || cache.has(href)) return false;

  const constructed = constructStylesheet(sheet);
  cache.set(href, constructed);

  return true;
};

const injectStylesheet = (target: StyleHost, sheet: CSSStyleSheet): void => {
  try {
    if (!('adoptedStyleSheets' in target)) return;
    const current = target.adoptedStyleSheets;
    if (current.includes(sheet)) return;
    target.adoptedStyleSheets = [...current, sheet];
  } catch (error) {
    console.error('Failed to inject stylesheet:', error);
  }
};

const injectAllStylesheets = (
  cache: Map<string, CSSStyleSheet>,
  target: StyleHost
): void => {
  cache.forEach(sheet => injectStylesheet(target, sheet));
};

/**
 * Manages stylesheet injection into adoptedStyleSheets
 */
export class StylesheetManager {
  private observer: MutationObserver | null = null;
  private readonly selector: string;
  private readonly stylesheetCache: Map<string, CSSStyleSheet>;
  private readonly processedElements: Set<Element>;

  constructor(selector = 'data-vanilla-extract') {
    this.selector = selector;
    this.stylesheetCache = new Map<string, CSSStyleSheet>();
    this.processedElements = new Set<Element>();
  }

  /**
   * Inject all cached styles into document and already-processed shadow roots
   */
  private injectCachedStylesIntoAllHosts(): void {
    injectAllStylesheets(this.stylesheetCache, document);
    this.processedElements.forEach(element => {
      const root = element.shadowRoot;
      if (!root) return;
      injectAllStylesheets(this.stylesheetCache, root);
    });
  }

  public init(): void {
    if (this.observer) return;
    // Initialize cache with existing stylesheets
    document
      .querySelectorAll<HTMLLinkElement>(`link[${this.selector}]`)
      .forEach(link => processStylesheet(this.stylesheetCache, link));

    // Ensure initial adoption without waiting for mutations
    this.injectCachedStylesIntoAllHosts();

    // Set up mutation observer for new stylesheets
    this.observer = new MutationObserver((mutations: MutationRecord[]) => {
      const newSheets = mutations
        .filter(({type}) => type === 'childList')
        .flatMap(({addedNodes}) => Array.from(addedNodes))
        .filter(
          (node): node is HTMLLinkElement =>
            node instanceof HTMLLinkElement && node.hasAttribute(this.selector)
        );

      if (newSheets.length === 0) return;

      const hasNewSheets = newSheets
        .filter(l => l.sheet) // only ready sheets
        .some(link => processStylesheet(this.stylesheetCache, link));

      // Handle async loading
      newSheets.forEach(link => {
        if (!link.sheet) {
          link.addEventListener(
            'load',
            () => {
              if (!processStylesheet(this.stylesheetCache, link)) return;
              this.injectCachedStylesIntoAllHosts();
            },
            {once: true}
          );
          return;
        }
      });

      if (!hasNewSheets) return;
      this.injectCachedStylesIntoAllHosts();
    });

    // Observe the document head for new stylesheets
    this.observer.observe(document.head, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Inject stylesheets into a target element
   */
  public injectIntoElement(element: Element): void {
    if (this.processedElements.has(element)) return;

    const target = element.shadowRoot ?? document;
    this.stylesheetCache.forEach(sheet => injectStylesheet(target, sheet));
    this.processedElements.add(element);
  }

  /**
   * Inject all cached stylesheets into document
   */
  public injectIntoDocument(): void {
    injectAllStylesheets(this.stylesheetCache, document);
  }

  /**
   * Clean up resources
   */
  public disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.processedElements.clear();
    this.stylesheetCache.clear();
  }
}

export const createStylesheetManager = (
  selector?: string
): StylesheetManager => {
  return new StylesheetManager(selector);
};
